import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../state/store";
import { getCookie } from "../../utils/helper";
import { validateMappingsAPI } from "./audioAPI";

interface ProcessAudioResponse {
  transcription: string;
  mappings: Record<string, any>[];
}

interface ApiError {
  message: string;
}

export interface Transcription {
  id: number;
  text: string;
}

export interface AudioState {
  audioBlob: Blob | null;
  transcription: Transcription[];
  isListening: boolean;
  loading: boolean;
  mappings: Record<string, any>[];
  error: string | null;
  isAudioMappingTableEnable: boolean;
}

const initialState: AudioState = {
  audioBlob: null,
  transcription: [],
  isListening: false,
  loading: false,
  mappings: [],
  error: null,
  isAudioMappingTableEnable: false,
};

export const processAudio = createAsyncThunk<
  ProcessAudioResponse,
  FormData,
  { rejectValue: ApiError }
>("audio/processAudio", async (formData: FormData, { rejectWithValue }) => {
  try {
    const token = getCookie("accessToken");
    const backendURL = import.meta.env.VITE_APP_BASE_URL || "";
    const response = await axios.post(
      `${backendURL}/speech_recognition/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data as ProcessAudioResponse;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || "Audio processing failed",
    });
  }
});

export const validateMappings = createAsyncThunk<
  any,
  Record<string, any>[],
  { rejectValue: ApiError }
>(
  "audio/validateMappings",
  async (mappings: Record<string, any>[], { rejectWithValue }) => {
    try {
      const response = await validateMappingsAPI(mappings);
      return response;
    } catch (error: any) {
      return rejectWithValue({
        message: error.response?.data?.message || "Validation failed",
      });
    }
  }
);

const audioSlice = createSlice({
  name: "audio",
  initialState,
  reducers: {
    startRecording: (state) => {
      state.isListening = true;
    },
    stopRecording: (state, action: PayloadAction<Blob>) => {
      state.audioBlob = action.payload;
      state.isListening = false;
    },
    resetAudio: (state) => {
      state.audioBlob = null;
      state.transcription = [];
      state.mappings = [];
      state.error = null;
    },
    setEnableAudioMappingTable: (state, action: PayloadAction<boolean>) => {
      state.isAudioMappingTableEnable = action.payload;
    },
    editMappingRow: (
      state,
      action: PayloadAction<{
        rowIndex: number;
        updatedRow: Record<string, any>;
      }>
    ) => {
      const { rowIndex, updatedRow } = action.payload;
      state.mappings[rowIndex] = updatedRow;
    },
    deleteMappingRow: (state, action: PayloadAction<number>) => {
      state.mappings.splice(action.payload, 1);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(processAudio.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(processAudio.fulfilled, (state, action) => {
      state.loading = false;

      const currentId = state.transcription.length
        ? state.transcription[state.transcription.length - 1].id
        : 0;

      const newTranscription = {
        id: currentId + 1,
        text: action.payload.transcription,
      };

      state.transcription = [...state.transcription, newTranscription];
      state.mappings = [...action.payload.mappings, ...state.mappings];
    });
    builder.addCase(processAudio.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Unknown error";
    });

    builder.addCase(validateMappings.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(validateMappings.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.mappings = [];
        state.mappings = action.payload || [];
      }
    });
    builder.addCase(validateMappings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Unknown error";
    });
  },
});

export const {
  startRecording,
  stopRecording,
  resetAudio,
  setEnableAudioMappingTable,
  editMappingRow,
  deleteMappingRow,
} = audioSlice.actions;

export const selectTranscript = (state: RootState) => state.audio.transcription;
export const selectIsListening = (state: RootState) => state.audio.isListening;
export const selectLoading = (state: RootState) => state.audio.loading;
export const selectMappings = (state: RootState) => state.audio.mappings;
export const selectError = (state: RootState) => state.audio.error;
export const selectEnableAudioMappingTable = (state: RootState) =>
  state.audio.isAudioMappingTableEnable;

export default audioSlice.reducer;
